// src/components/admin/ClientProductCodesPage.js
import React, { useState, useEffect } from 'react';
import { 
  Card, Form, Input, Button, Table, message, Popconfirm, Space, 
  Typography, Tooltip, Modal, Upload, Divider, Alert 
} from 'antd';
import { PlusOutlined, DeleteOutlined, UploadOutlined, EditOutlined } from '@ant-design/icons';
import { catalogService } from '../../services/api';
import { useAuth } from '../../context/AuthContext'; 

const { Title, Text } = Typography;

const ClientProductCodesPage = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [editingCode, setEditingCode] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [formError, setFormError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filteredCodes, setFilteredCodes] = useState([]);

  const permissions = user?.role?.profile?.permissions;
  const canViewPage = permissions?.pages?.viewClientCodes; 
  const canCreate = permissions?.actions?.clientCodes?.canCreate;
  const canEdit = permissions?.actions?.clientCodes?.canEdit;
  const canDelete = permissions?.actions?.clientCodes?.canDelete;
  const canUpload = permissions?.actions?.clientCodes?.canUpload;

  // <<< INICIO: Función para normalizar texto (quitar acentos y a minúsculas) >>>
  const normalizeText = (text) => {
    return text
      .toString()
      .normalize("NFD") // Separa los caracteres base de los acentos
      .replace(/[\u0300-\u036f]/g, "") // Elimina los acentos
      .toLowerCase(); // Convierte a minúsculas
  };
  // <<< FIN >>>

  useEffect(() => {
    if (canViewPage) {
      fetchClientCodes();
    }
  }, [canViewPage]);

  useEffect(() => {
    // <<< INICIO: Se utiliza la función de normalización para el filtro >>>
    const normalizedSearchText = normalizeText(searchText);
    const filteredData = codes.filter(item =>
      Object.keys(item).some(key =>
        normalizeText(String(item[key])).includes(normalizedSearchText)
      )
    );
    setFilteredCodes(filteredData);
    // <<< FIN >>>
  }, [searchText, codes]);

  const fetchClientCodes = async () => {
    setLoading(true);
    try {
      const response = await catalogService.getClientCodes();
      if (response.data.success) {
        setCodes(response.data.data);
      } else {
        message.error(response.data.message || 'Error al cargar las equivalencias');
      }
    } catch (error) {
      // El interceptor maneja el error
    } finally {
      setLoading(false);
    }
  };

  const showModal = (record = null) => {
    setUploadResult(null); 
    setFormError(null); 
    if (record) {
        setEditingCode(record);
        form.setFieldsValue(record);
    } else {
        setEditingCode(null);
        form.resetFields();
    }
    setIsModalOpen(true);
  };
  
  const handleCancel = () => {
      setIsModalOpen(false);
      setEditingCode(null);
      form.resetFields();
      setFormError(null);
  };

  const handleDeleteCode = async (id) => {
    setLoading(true);
    try {
      await catalogService.deleteClientCode(id);
      message.success('Equivalencia eliminada correctamente');
      fetchClientCodes();
    } catch (error) {
        message.error(error.response?.data?.message || 'Error al eliminar la equivalencia');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCode = async (values) => {
    setLoading(true);
    setFormError(null);
    try {
      if (editingCode) {
        await catalogService.updateClientCode(editingCode._id, values);
        message.success('Equivalencia actualizada correctamente');
      } else {
        await catalogService.createClientCode(values);
        message.success('Equivalencia guardada correctamente');
      }
      handleCancel();
      fetchClientCodes();
    } catch (error) {
        const errorMessage = error.response?.data?.message || 'Error al guardar la equivalencia';
        if (error.response?.status === 409) {
            setFormError(errorMessage);
        } else {
            message.error(errorMessage);
        }
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
        message.error('Por favor, seleccione un archivo para subir.');
        return;
    }
    const formData = new FormData();
    formData.append('file', fileList[0]);

    setUploading(true);
    setUploadResult(null); 
    try {
        const response = await catalogService.uploadClientCodes(formData);
        setUploadResult({
            type: 'success',
            message: `${response.data.message} Registros insertados: ${response.data.data.inserted}, Registros actualizados: ${response.data.data.updated}.`
        });
        setFileList([]);
        fetchClientCodes();
    } catch (error) {
        setUploadResult({
            type: 'error',
            message: error.response?.data?.message || 'Ocurrió un error inesperado durante la carga masiva.'
        });
    } finally {
        setUploading(false);
    }
  };

  const uploadProps = {
    onRemove: () => {
        setFileList([]);
        setUploadResult(null);
    },
    beforeUpload: (file) => {
      const isXlsx = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.name.endsWith('.xlsx');
      if (!isXlsx) {
        message.error('¡Solo puedes subir archivos .xlsx!');
      } else {
        setFileList([file]);
        setUploadResult(null); 
      }
      return false;
    },
    fileList,
  };

  const columns = [
    { title: 'Código SAP (itemCode)', dataIndex: 'itemCode', key: 'itemCode', sorter: (a, b) => a.itemCode.localeCompare(b.itemCode) },
    { title: 'Código Cliente (cardCode)', dataIndex: 'cardCode', key: 'cardCode', sorter: (a, b) => a.cardCode.localeCompare(b.cardCode) },
    { title: 'Código Equivalente', dataIndex: 'clientItemCode', key: 'clientItemCode' },
    { title: 'Descripción', dataIndex: 'description', key: 'description' },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      render: (_, record) => (
        <Space>
          {canEdit && (
             <Tooltip title="Editar"><Button type="primary" shape="circle" icon={<EditOutlined />} onClick={() => showModal(record)} /></Tooltip>
          )}
          {canDelete && (
            <Popconfirm title="¿Seguro de eliminar?" onConfirm={() => handleDeleteCode(record._id)} okText="Sí" cancelText="No">
              <Tooltip title="Eliminar"><Button type="primary" danger shape="circle" icon={<DeleteOutlined />} /></Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  if (!canViewPage) {
    return <Card><p>No tiene permiso para acceder a este catálogo.</p></Card>;
  }

  return (
    <Card title={<Title level={4}>Catálogo de Códigos de Artículos por Cliente</Title>}>
      <Space direction="vertical" style={{ width: '100%' }}>
        
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>Agregar Equivalencia</Button>
        )}

        {canUpload && (
            <>
                <Divider>O</Divider>
                <Card type="inner" title="Carga Masiva desde Excel">
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Text>El archivo debe ser .xlsx y tener las columnas: <Text code>itemCode</Text>, <Text code>cardCode</Text>, <Text code>clientItemCode</Text>, y opcionalmente <Text code>description</Text>.</Text>
                        <Space>
                            <Upload {...uploadProps} maxCount={1}><Button icon={<UploadOutlined />}>Seleccionar Archivo</Button></Upload>
                            <Button type="primary" onClick={handleUpload} disabled={fileList.length === 0} loading={uploading}>{uploading ? 'Cargando...' : 'Iniciar Carga'}</Button>
                        </Space>
                        {uploadResult && (
                            <Alert
                                message={uploadResult.message}
                                type={uploadResult.type}
                                showIcon
                                closable
                                onClose={() => setUploadResult(null)}
                                style={{ marginTop: 16 }}
                            />
                        )}
                    </Space>
                </Card>
            </>
        )}
      </Space>

      <Divider />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <Input.Search
          placeholder="Buscar en el catálogo..."
          onChange={e => setSearchText(e.target.value)}
          style={{ maxWidth: 400, flexGrow: 1 }}
        />
        <Text type="secondary" style={{ marginLeft: '16px', whiteSpace: 'nowrap' }}>
            {searchText
             ? `Mostrando ${filteredCodes.length} de ${codes.length} registros`
             : `Total de registros: ${codes.length}`
            }
        </Text>
      </div>

      <Table
        columns={columns}
        dataSource={filteredCodes}
        rowKey="_id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
      />

      <Modal
        title={editingCode ? "Editar Equivalencia" : "Agregar Nueva Equivalencia"}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSaveCode}>
          <Form.Item name="itemCode" label="Código SAP (itemCode)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="cardCode" label="Código Cliente (cardCode)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="clientItemCode" label="Código Equivalente del Cliente" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Descripción (Opcional)">
            <Input.TextArea />
          </Form.Item>
          {formError && (
              <Alert 
                message={formError} 
                type="error" 
                showIcon 
                style={{ marginBottom: 16 }} 
              />
          )}
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>{editingCode ? "Actualizar" : "Guardar"}</Button>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ClientProductCodesPage;
